import React, { useState, useRef, useEffect } from 'react';
import { FiChevronDown } from 'react-icons/fi';

interface Country {
  id: number;
  code: string;
  name: string;
  name_fr?: string;
  enabled: boolean;
}

interface CountrySelectorProps {
  countries: Country[];
  value: string;
  onChange: (name: string) => void;
  disabled?: boolean;
  loading?: boolean;
  placeholder?: string;
}

const CountrySelector: React.FC<CountrySelectorProps> = ({
  countries,
  value,
  onChange,
  disabled = false,
  loading = false,
  placeholder = 'Select country...'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Filter countries based on search term
  const filteredCountries = countries.filter(
    country =>
      country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      country.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get selected country display
  const selectedCountry = countries.find(c => c.name === value);
  const displayValue = selectedCountry 
    ? selectedCountry.name
    : loading 
    ? 'Loading...' 
    : value || placeholder;

  const handleSelect = (name: string) => {
    onChange(name);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div ref={dropdownRef} className="relative w-full">
      {/* Selector Button */}
      <button
        type="button"
        onClick={() => !disabled && !loading && setIsOpen(!isOpen)}
        disabled={disabled || loading}
        className="flex h-10 w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm outline-none transition-all hover:border-brand-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white disabled:opacity-50"
      >
        <span className="truncate text-left text-sm text-gray-900 dark:text-white">
          {displayValue}
        </span>
        <FiChevronDown
          className={`ml-2 h-5 w-5 flex-shrink-0 text-gray-400 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute left-0 top-full z-50 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-xl dark:border-navy-600 dark:bg-navy-800">
          {/* Search Input */}
          <div className="border-b border-gray-200 p-2 dark:border-navy-600">
            <input
              type="text"
              placeholder="Search country..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-900 dark:text-white"
              autoFocus
            />
          </div>

          {/* Country List */}
          <div className="max-h-60 overflow-y-auto">
            {filteredCountries.length > 0 ? (
              filteredCountries.map((country) => (
                <button
                  key={country.id}
                  type="button"
                  onClick={() => handleSelect(country.name)}
                  className={`flex w-full items-center px-4 py-3 text-left text-sm transition-colors hover:bg-brand-50 dark:hover:bg-brand-900/20 ${
                    value === country.name
                      ? 'bg-brand-100 text-brand-600 dark:bg-brand-900/30 dark:text-brand-400'
                      : 'text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <span className="truncate">{country.name}</span>
                </button>
              ))
            ) : (
              <div className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                No countries found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CountrySelector;
